import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Link } from "react-router-dom";

import { useTrans } from '~/hooks/useTrans';
import { ICatalogItem } from "~/providers/interfaces";
import Loader from '~/components/Loader';
import { getLink } from "~/providers/route";
import ExternalLink from "~/components/ExternalLink";
import MyCheckbox from "~/mui/MyCheckbox";
import { formatDate } from "~/utils/utils";
import SortArrows, { TSortArrowSelect } from "~/components/SortArrows";
import { IAdminCatalogsFilter } from "~/providers/admin-interfaces";

import "./style.less";

interface IAdminCatalogsTable {
	filter: IAdminCatalogsFilter;
	isCzech: boolean;
	loading: boolean;
	items: Array<ICatalogItem>;
	onSortUpdate: (newFilter: IAdminCatalogsFilter) => void;
}

const COLUMNS = 8;

export default function AdminCatalogsTable({
	filter,
	isCzech = true,
	loading = true,
	items = [],
	onSortUpdate,
}: IAdminCatalogsTable) {
	const { trans, transCatalogQuality, transCatalogApproved, formatCredits } = useTrans();

	function getSelect(filterName: keyof IAdminCatalogsFilter): TSortArrowSelect {
		if (filterName in filter) {
			return filter[filterName] === "ASC"
				? "up"
				: "down";
		}

		return "none";
	}

	function onSelectClick(arrow: TSortArrowSelect, filterName: keyof IAdminCatalogsFilter) {
		onSortUpdate({
			...arrow === "none"
				? {}
				: {
					[filterName]: arrow === "up"
						? "ASC"
						: "DESC",
				},
		});
	}

	return <TableContainer component={Paper}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					<TableCell sx={{ fontWeight: "bold" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminCatalogsTable.project") }
							<SortArrows select={getSelect("sortByInternalName")} onClick={arrow => onSelectClick(arrow, "sortByInternalName")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold" }}>
						{ trans("adminCatalogsTable.referenceUrl") }
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminProjectCatalogModal.quality.title") }
							<SortArrows select={getSelect("sortByQuality")} onClick={arrow => onSelectClick(arrow, "sortByQuality")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminCatalogsTable.approvedByAdmin") }
							<SortArrows select={getSelect("sortByApproved")} onClick={arrow => onSelectClick(arrow, "sortByApproved")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						{ trans("adminCatalogsTable.preview") }
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminCatalogsTable.publish") }
							<SortArrows select={getSelect("sortByPublished")} onClick={arrow => onSelectClick(arrow, "sortByPublished")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminCatalogsTable.previousMonthConsumption") }
							<SortArrows select={getSelect("sortByPreviousMonthConsumption")} onClick={arrow => onSelectClick(arrow, "sortByPreviousMonthConsumption")} />
						</div>
					</TableCell>
					<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
						<div className="adminProjectsTable__headerWithSort no-tac">
							{ trans("adminCatalogsTable.lastUpdate") }
							<SortArrows select={getSelect("sortByLastUpdate")} onClick={arrow => onSelectClick(arrow, "sortByLastUpdate")} />
						</div>
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{ items.map(catalog => (<TableRow key={catalog.id}>
					<TableCell>
						<Link to={getLink("project-catalog", true, { projectId: catalog.projectId })}>
							{ catalog.internalName }
						</Link>
					</TableCell>
					<TableCell>
						<ExternalLink to={catalog.referenceUrl}>
							{ catalog.referenceUrl }
						</ExternalLink>
					</TableCell>
					<TableCell className="adminCatalogsTable__center">
						{ transCatalogQuality(catalog.quality) }
					</TableCell>
					<TableCell className="adminCatalogsTable__center">
						<span style={{ color: catalog.approvedByAdmin ? "var(--dark-green-text-color)" : "var(--red-text-color)" }}>
							{ transCatalogApproved(catalog.approvedByAdmin) }
						</span>
					</TableCell>
					<TableCell>
						<div className="adminCatalogsTable__previewCover">
							<img src={catalog.thumbnailUrl} alt="" className="adminCatalogsTable__previewImage" />
						</div>
					</TableCell>
					<TableCell className="adminCatalogsTable__center">
						<MyCheckbox checked={catalog.published} label="" onChange={() => {}} />
					</TableCell>
					<TableCell className="adminCatalogsTable__center">
						{ formatCredits(catalog.previousMonthConsumption) }
					</TableCell>
					<TableCell className="adminCatalogsTable__center">
						{ formatDate(new Date(catalog.lastUpdate), isCzech) }
					</TableCell>
				</TableRow>)) }
				{ items.length === 0 && <TableRow>
					<TableCell colSpan={COLUMNS} style={{ textAlign: "center" }}>
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
